import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

import {
  CreateCalendarEventRequest,
  CalendarEventDto,
  ParsedError,
} from "../../interfaces";
import {
  useAddCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
  useUploadCalendarEventImageMutation,
} from "../../store/api/calendarApi";
import { ActivityButton } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import { MediaDto } from "../../interfaces/media";
import { toast } from "react-toastify";
import { CategorySelect } from "../../components/Selects/CategorySelect";
import { CategoryTypeEnum, ErrorHandlingTypeEnum } from "../../enums";
import { IMAGE_ACCEPT } from "../../constants";

import imagePlaceholder from "../../assets/images/placeholder/image-placeholder.png";

type TProps = {
  calendarEvent: CalendarEventDto;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditCalendarEventModal: React.FC<TProps> = ({
  calendarEvent,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateCalendarEventRequest>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateValue, setDateValue] = useState([
    calendarEvent?.startAt,
    calendarEvent?.endAt,
  ]);

  //#region api
  const [
    addCalendarEvent,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddCalendarEventMutation();

  const [
    updateCalendarEvent,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateCalendarEventMutation();

  const [
    deleteCalendarEvent,
    { isLoading: isLoadingDelete, error: errorDelete, isError: isErrorDelete },
  ] = useDeleteCalendarEventMutation();

  const [
    uploadImageApi,
    {
      isLoading: isLoadingUploadImage,
      isSuccess: isSuccessUploadImage,
      data: dataUploadImage,
      isError: isErrorUploadImage,
      error: errorUploadImage,
    },
  ] = useUploadCalendarEventImageMutation();
  //#endregion

  //#region form
  const form = useFormik<CreateCalendarEventRequest>({
    enableReinitialize: true,
    initialValues: {
      title: {
        en: request?.title.en || "",
        ar: request?.title.ar || "",
      },
      allDay: request?.allDay || false,
      startAt: request?.startAt,
      endAt: request?.endAt,
      imageId: "",
      categoryId: request?.categoryId || "",
    },
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      allDay: Yup.boolean().required(),
      categoryId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "category",
        }),
      ),
      imageId: Yup.string().required(t("isNotEmpty", { displayName: "image" })),
    }),
    onSubmit: async (values) => {
      if (calendarEvent.id) {
        await updateCalendarEvent({
          id: calendarEvent.id,
          data: { ...values, startAt: dateValue[0], endAt: dateValue[1] },
        });
      } else {
        await addCalendarEvent({
          ...values,
          startAt: dateValue[0],
          endAt: dateValue[1],
        });
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (!isLoadingUploadImage && isSuccessUploadImage && dataUploadImage) {
      form.setFieldValue("imageId", (dataUploadImage as MediaDto).id);
    }
  }, [isLoadingUploadImage, isSuccessUploadImage, isSuccessUploadImage]);

  useEffect(() => {
    form.setValues({
      title: calendarEvent?.title || { en: "", ar: "" },
      startAt: calendarEvent?.startAt,
      endAt: calendarEvent?.endAt,
      allDay: calendarEvent?.allDay,
      imageId: calendarEvent?.imageId || "",
      categoryId: calendarEvent?.category?.id || "",
    });
    setDateValue([calendarEvent?.startAt, calendarEvent?.endAt]);
  }, [calendarEvent]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingDelete && isErrorDelete) _error = errorDelete as ParsedError;
    if (!isLoadingUploadImage && isErrorUploadImage)
      _error = errorUploadImage as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate, isLoadingDelete, isLoadingUploadImage]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  //#region handlers
  const onChangeImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/,
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      uploadImageApi({ file: imageFile, id: calendarEvent?.id });
    }
  };

  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onDelete = () => {
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    await deleteCalendarEvent(calendarEvent?.id as string).unwrap();
    onClose();
    setShowDeleteModal(false);
  };

  const onChangeDate = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateValue(dates);
    } else {
      const startAt = dates[0];
      const endAt = dates[0];
      startAt.setHours(0, 0, 0, 0);
      endAt.setHours(23, 59, 59, 999);
      setDateValue([startAt, endAt]);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {calendarEvent && calendarEvent?.id
              ? t("editDeleteEvent")
              : t("addItem", { item: "calendarEvent" })}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="date" className="form-label">
                {t("dateTime")}
                <span className="text-danger">*</span>
              </label>
              <Flatpickr
                className="form-control dash-filter-picker shadow flatpickr-border"
                options={{
                  mode: "range",
                  enableTime: true,
                  dateFormat: "Y-m-d h:i K",
                }}
                value={dateValue}
                onClose={(e) => onChangeDate(e)}
              />
            </div>
            <Row>
              <Col sm={12}>
                <div className="col-auto text-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        dataUploadImage?.path ||
                        calendarEvent?.image ||
                        imagePlaceholder
                      }
                      className=" avatar-xl img-thumbnail user-profile-image"
                      alt="user-profile"
                    />
                    <div className="avatar-xl p-0 rounded-circle profile-photo-edit">
                      <Input
                        id="banner_image"
                        type="file"
                        className="profile-img-file-input"
                        accept={IMAGE_ACCEPT}
                        onChange={onChangeImage}
                        disabled={isLoadingUploadImage}
                      />
                      <Label
                        htmlFor="banner_image"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body">
                          {isLoadingUploadImage && <Spinner />}
                          {!isLoadingUploadImage && (
                            <i className="ri-camera-fill"></i>
                          )}
                        </span>
                      </Label>
                    </div>
                  </div>
                  {form.touched.imageId && form.errors.imageId ? (
                    <FormFeedback type="invalid" className="d-block">
                      {form.errors.imageId}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label htmlFor="categoryId" className="form-label">
                {t("category")}
                <span className="text-danger">*</span>
              </Label>
              <CategorySelect
                type="select"
                categoryType={CategoryTypeEnum.CALENDAR_EVENT}
                name="categoryId"
                className="form-select"
                aria-label={t("selectCategory")}
                placeholder={t("selectCategory")}
                value={form.values.categoryId}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.categoryId && form.errors.categoryId
                    ? true
                    : false
                }
              ></CategorySelect>
              {form.touched.categoryId && form.errors.categoryId ? (
                <FormFeedback type="invalid" className="d-block">
                  {form.errors.categoryId}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                autoComplete="off"
                id="title.en"
                name="title.en"
                value={form.values?.title?.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="title.ar" className="form-label">
                {t("titleAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                autoComplete="off"
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            {calendarEvent && calendarEvent?.id && (
              <ActivityButton
                onClick={onDelete}
                title="deleteEvent"
                inProgressTitle="deleting"
                color="success"
                className="btn btn-danger"
                type="button"
                inProgress={isLoadingDelete}
                disabled={isLoadingAdd || isLoadingUpdate || isLoadingDelete}
              ></ActivityButton>
            )}
            <ActivityButton
              title="saveEvent"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingDelete ||
                isLoadingUploadImage
              }
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingDelete ||
                isLoadingUploadImage
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
      />
    </React.Fragment>
  );
};
